import { openTelegramLink } from '@telegram-apps/sdk-react';
import { useNavigate } from 'react-router';
import type { Locale } from '@debtbot/shared';
import { useMe, useUpdateMe } from '../api/hooks';
import { Segmented } from '../components/Segmented';
import { Button } from '../components/Button';
import { ErrorBanner } from '../components/ErrorBanner';
import { ListSkeleton } from '../components/Skeleton';
import { useT } from '../i18n';
import { BOT_USERNAME } from '../lib/bot-username';
import { haptic } from '../telegram/haptic';

const VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev';

export function SettingsScreen() {
  const t = useT();
  const me = useMe();
  const update = useUpdateMe();
  const navigate = useNavigate();

  const openBot = () => {
    const url = `https://t.me/${BOT_USERNAME}`;
    try {
      openTelegramLink(url);
    } catch {
      window.open(url, '_blank');
    }
  };

  return (
    <main className="min-h-dvh bg-bg text-text px-4 py-4 flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t.settings.title}</h1>
      {me.isPending && <ListSkeleton rows={2} />}
      {me.isError && <ErrorBanner error={me.error} onRetry={() => void me.refetch()} />}
      {me.data && (
        <section className="flex flex-col gap-3">
          <label className="flex items-center justify-between gap-3 rounded-xl bg-secondary-bg px-4 py-3">
            <div>
              <div>{t.settings.notifications}</div>
              <div className="text-xs text-hint">{t.settings.notificationsHint}</div>
            </div>
            <input
              type="checkbox"
              className="h-6 w-6 shrink-0 accent-[var(--tg-theme-button-color)]"
              checked={me.data.notifications_enabled}
              disabled={update.isPending}
              onChange={(e) => {
                haptic.select();
                update.mutate({ notifications_enabled: e.target.checked });
              }}
            />
          </label>
          <div className="flex flex-col gap-2 rounded-xl bg-secondary-bg px-4 py-3">
            <span>{t.settings.language}</span>
            <Segmented<'auto' | Locale>
              value={me.data.preferred_language ?? 'auto'}
              onChange={(v) => {
                haptic.select();
                update.mutate({ language: v === 'auto' ? null : v });
              }}
              options={[
                { value: 'auto', label: t.settings.languageAuto },
                { value: 'ru', label: t.settings.languageRu },
                { value: 'uz', label: t.settings.languageUz },
              ]}
            />
          </div>
          <button
            type="button"
            data-testid="feedback-link"
            onClick={() => void navigate('/feedback')}
            className="flex items-center justify-between gap-3 rounded-xl bg-secondary-bg px-4 py-3 text-left active:opacity-70"
          >
            <div>
              <div>💡 {t.settings.feedback}</div>
              <div className="text-xs text-hint">{t.settings.feedbackHint}</div>
            </div>
            <span className="text-hint">›</span>
          </button>
          <div className="flex items-center justify-between rounded-xl bg-secondary-bg px-4 py-3">
            <span>{t.settings.version}</span>
            <span className="text-hint">{VERSION}</span>
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <div className="text-sm text-hint">{t.settings.deleteHint}</div>
            <Button variant="secondary" onClick={openBot}>
              {t.settings.openBot}
            </Button>
          </div>
        </section>
      )}
    </main>
  );
}
